import { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { PASSWORD_HINT, validatePassword } from '@shared/validate';
import { Badge } from '../../components/Badge';
import { Button } from '../../components/Button';
import { Card, CardBody, CardHeader, CardTitle } from '../../components/Card';
import { TextInput } from '../../components/Field';
import { PageContainer } from '../../app/AppShell';
import { errorMessage } from '../../lib/api';
import { useAuthStore } from '../../app/authStore';

/** 修改口令。强制改密时不允许取消，也不允许跳到别处。 */
export function ChangePasswordPage() {
  const mustChange = useAuthStore((state) => state.mustChangePassword);
  const changePassword = useAuthStore((state) => state.changePassword);
  const navigate = useNavigate();
  const location = useLocation();
  const forced = mustChange || Boolean((location.state as { forced?: boolean } | null)?.forced);

  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const policyError = newPassword === '' ? null : validatePassword(newPassword)?.message ?? null;
  const mismatch = confirmPassword !== '' && confirmPassword !== newPassword ? '两次输入的新口令不一致' : null;
  const canSubmit = currentPassword !== '' && newPassword !== '' && policyError === null && mismatch === null && !submitting;

  const onSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      await changePassword(currentPassword, newPassword);
      navigate('/', { replace: true });
    } catch (submitError) {
      setError(errorMessage(submitError));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <PageContainer className="max-w-xl">
      {forced ? (
        <div className="rounded-lg border border-warning/50 bg-warning-soft px-3.5 py-3 text-xs leading-relaxed text-warning">
          <p className="font-medium">首次登录必须修改口令</p>
          <p className="mt-1 opacity-90">
            初始口令写在部署配置里，属于共享秘密。修改后其它设备上的会话会自动失效，需要重新登录。
          </p>
        </div>
      ) : null}

      <Card>
        <CardHeader>
          <CardTitle subtitle={PASSWORD_HINT}>修改口令</CardTitle>
          {forced ? <Badge tone="warning">必须完成</Badge> : null}
        </CardHeader>
        <CardBody>
          <form onSubmit={onSubmit} className="flex flex-col gap-3">
            <TextInput
              label="当前口令"
              type="password"
              autoComplete="current-password"
              value={currentPassword}
              onChange={(event) => setCurrentPassword(event.target.value)}
              required
            />
            <TextInput
              label="新口令"
              type="password"
              autoComplete="new-password"
              value={newPassword}
              onChange={(event) => setNewPassword(event.target.value)}
              error={policyError ?? undefined}
              hint={PASSWORD_HINT}
              required
            />
            <TextInput
              label="确认新口令"
              type="password"
              autoComplete="new-password"
              value={confirmPassword}
              onChange={(event) => setConfirmPassword(event.target.value)}
              error={mismatch ?? undefined}
              required
            />
            {error ? <p className="text-xs text-critical">{error}</p> : null}
            <div className="flex items-center gap-2">
              <Button type="submit" variant="primary" loading={submitting} disabled={!canSubmit}>
                提交
              </Button>
              {!forced ? (
                <Button variant="ghost" onClick={() => navigate(-1)}>
                  取消
                </Button>
              ) : null}
            </div>
          </form>
        </CardBody>
      </Card>
    </PageContainer>
  );
}
